import { toast } from 'sonner';
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import hosys from "@/api/api";
import { RecipeBasicInfo } from "./RecipeBasicInfo";
import { RecipeIngredients } from "./RecipeIngredients";
import { RecipePricing } from "./RecipePricing";
import { RecipeNutrition } from "./RecipeNutrition";

const EMPTY_FORM = {
  name: "",
  category: "main",
  reference: "",
  ean13: "",
  ean13c: "",
  ean14: "",
  description: "",
  weight_unit: "0",
  size: "",
  photo_url: "",
  ingredients: [{ name: "", quantity: "", unit: "g", unit_cost: "" }],
  allergens: [],
  preparation_steps: "",
  preparation_time: "0",
  estimated_cost: "0",
  selling_price: "0",
  shelf_life_months: "0",
  storage_temperature: "",
  storage_notes: "",
  nutritional_info: {
    energy_kcal: "0",
    energy_kj: "0",
    fats_g: "0",
    saturated_fats_g: "0",
    carbohydrates_g: "0",
    sugars_g: "0",
    proteins_g: "0",
    fiber_g: "0",
    salt_g: "0",
  },
  microbiological_tests: {
    salmonella: "",
    listeria: "",
  },
  organoleptic_characteristics: {
    color: "",
    flavor: "",
    smell: "",
    texture: "",
    appearance: "",
  },
};

export default function RecipeForm({ open, onClose, onSave, venueId, editData }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [generatingDetails, setGeneratingDetails] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);
  const [ingredientCatalog, setIngredientCatalog] = useState([]);
  const [ingSearch, setIngSearch] = useState({});

  useEffect(() => {
    if (!venueId || !open) return;
    hosys.entities.Ingredient.filter({ venue_id: venueId, active: true })
      .then(items => setIngredientCatalog(items.sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => {});
  }, [venueId, open]);

  // Computed values
  const totalIngGrams = form.ingredients
    .filter(i => i.name && (i.unit === "g" || !i.unit))
    .reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0);

  const hasIngCosts = form.ingredients.some(i => i.name && parseFloat(i.unit_cost) > 0);
  const autoIngCost = form.ingredients
    .filter(i => i.name && parseFloat(i.unit_cost) > 0)
    .reduce((s, i) => s + (parseFloat(i.unit_cost) || 0), 0);
  const effectiveCost = hasIngCosts ? autoIngCost : (parseFloat(form.estimated_cost) || 0);

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La fotografia no pot superar 5MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const response = await hosys.functions.invoke('uploadFile', {
        file_base64: base64,
        file_name: file.name,
        file_type: file.type,
      });
      const fileUrl = response?.data?.file_url;
      if (!fileUrl) throw new Error('No s\'ha pogut pujar la fotografia');
      setForm(prev => ({ ...prev, photo_url: fileUrl }));
      if (photoInputRef.current) photoInputRef.current.value = '';
    } catch (err) {
      console.error('Error uploading photo:', err);
      toast.error('Error al pujar la fotografia: ' + (err.message || err));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Init form from editData
  useEffect(() => {
    if (editData) {
      setForm({
        ...EMPTY_FORM,
        ...editData,
        reference: editData.reference || "",
        ean13: editData.ean13 || "",
        ean13c: editData.ean13c || "",
        ean14: editData.ean14 || "",
        description: editData.description || "",
        size: editData.size || "",
        photo_url: editData.photo_url || "",
        preparation_steps: editData.preparation_steps || "",
        preparation_time: editData.preparation_time ? String(editData.preparation_time) : "0",
        estimated_cost: editData.estimated_cost ? String(editData.estimated_cost) : "0",
        selling_price: editData.selling_price ? String(editData.selling_price) : "0",
        weight_unit: editData.weight_unit ? String(editData.weight_unit) : "0",
        shelf_life_months: editData.shelf_life_months ? String(editData.shelf_life_months) : "0",
        storage_temperature: editData.storage_temperature || "",
        storage_notes: editData.storage_notes || "",
        ingredients: editData.ingredients?.length > 0
          ? editData.ingredients.map(i => ({ ...i, unit_cost: i.unit_cost !== undefined ? String(i.unit_cost) : "" }))
          : [{ name: "", quantity: "", unit: "g", unit_cost: "" }],
        allergens: editData.allergens || [],
        nutritional_info: {
          energy_kcal: editData.nutritional_info?.energy_kcal ? String(editData.nutritional_info.energy_kcal) : "0",
          energy_kj: editData.nutritional_info?.energy_kj ? String(editData.nutritional_info.energy_kj) : "0",
          fats_g: editData.nutritional_info?.fats_g ? String(editData.nutritional_info.fats_g) : "0",
          saturated_fats_g: editData.nutritional_info?.saturated_fats_g ? String(editData.nutritional_info.saturated_fats_g) : "0",
          carbohydrates_g: editData.nutritional_info?.carbohydrates_g ? String(editData.nutritional_info.carbohydrates_g) : "0",
          sugars_g: editData.nutritional_info?.sugars_g ? String(editData.nutritional_info.sugars_g) : "0",
          proteins_g: editData.nutritional_info?.proteins_g ? String(editData.nutritional_info.proteins_g) : "0",
          fiber_g: editData.nutritional_info?.fiber_g ? String(editData.nutritional_info.fiber_g) : "0",
          salt_g: editData.nutritional_info?.salt_g ? String(editData.nutritional_info.salt_g) : "0",
        },
        microbiological_tests: {
          salmonella: editData.microbiological_tests?.salmonella || "",
          listeria: editData.microbiological_tests?.listeria || "",
        },
        organoleptic_characteristics: {
          color: editData.organoleptic_characteristics?.color || "",
          flavor: editData.organoleptic_characteristics?.flavor || "",
          smell: editData.organoleptic_characteristics?.smell || "",
          texture: editData.organoleptic_characteristics?.texture || "",
          appearance: editData.organoleptic_characteristics?.appearance || "",
        },
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editData, open]);

  // Ingredient handlers
  const addIngredient = () => {
    setForm({ ...form, ingredients: [...form.ingredients, { name: "", quantity: "", unit: "g", unit_cost: "" }] });
  };

  const removeIngredient = (idx) => {
    setForm({ ...form, ingredients: form.ingredients.filter((_, i) => i !== idx) });
    setIngSearch(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };

  const updateIngredient = (idx, field, value) => {
    const updated = [...form.ingredients];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, ingredients: updated });
  };

  const handleIngCostBlur = async (idx) => {
    const ing = form.ingredients[idx];
    if (!ing.name || !ing.unit_cost) return;
    const newCost = parseFloat(ing.unit_cost);
    if (!newCost || newCost <= 0) return;
    const catalogIng = ingredientCatalog.find(
      c => c.name.toLowerCase() === ing.name.toLowerCase()
    );
    if (!catalogIng) return;
    if (Math.abs((catalogIng.unit_price || 0) - newCost) < 0.0001) return;
    await hosys.entities.Ingredient.update(catalogIng.id, { unit_price: newCost, previous_price: catalogIng.unit_price });
    setIngredientCatalog(prev => prev.map(c => c.id === catalogIng.id ? { ...c, unit_price: newCost } : c));
    const allRecipes = await hosys.entities.RecipeSheet.filter({ venue_id: venueId });
    const toUpdate = allRecipes.filter(r =>
      r.id !== editData?.id &&
      r.ingredients?.some(i => i.name.toLowerCase() === ing.name.toLowerCase())
    );
    await Promise.all(toUpdate.map(r => {
      const updatedIngs = r.ingredients.map(i =>
        i.name.toLowerCase() === ing.name.toLowerCase() ? { ...i, unit_cost: newCost } : i
      );
      return hosys.entities.RecipeSheet.update(r.id, { ingredients: updatedIngs });
    }));
  };

  const selectFromCatalog = (idx, catalogIng) => {
    const updated = [...form.ingredients];
    updated[idx] = {
      ...updated[idx],
      name: catalogIng.name,
      unit: catalogIng.unit,
      unit_cost: String(catalogIng.unit_price),
    };
    setForm({ ...form, ingredients: updated });
    setIngSearch(prev => ({ ...prev, [idx]: "" }));
  };

  // Save handler
  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const validIngredients = form.ingredients.filter(i => i.name);
      const finalCost = autoIngCost > 0
        ? autoIngCost
        : (form.estimated_cost ? Number(form.estimated_cost) : undefined);
      await onSave({
        ...form,
        venue_id: venueId,
        ingredients: validIngredients.map(i => ({
          ...i,
          unit_cost: i.unit_cost ? Number(i.unit_cost) : undefined,
          quantity: i.quantity || undefined,
        })),
        preparation_time: form.preparation_time ? Number(form.preparation_time) : undefined,
        estimated_cost: finalCost,
        selling_price: form.selling_price ? Number(form.selling_price) : undefined,
        weight_unit: totalIngGrams > 0 ? totalIngGrams : (form.weight_unit ? Number(form.weight_unit) : undefined),
        shelf_life_months: form.shelf_life_months ? Number(form.shelf_life_months) : undefined,
        nutritional_info: {
          energy_kcal: form.nutritional_info.energy_kcal ? Number(form.nutritional_info.energy_kcal) : undefined,
          energy_kj: form.nutritional_info.energy_kj ? Number(form.nutritional_info.energy_kj) : undefined,
          fats_g: form.nutritional_info.fats_g ? Number(form.nutritional_info.fats_g) : undefined,
          saturated_fats_g: form.nutritional_info.saturated_fats_g ? Number(form.nutritional_info.saturated_fats_g) : undefined,
          carbohydrates_g: form.nutritional_info.carbohydrates_g ? Number(form.nutritional_info.carbohydrates_g) : undefined,
          sugars_g: form.nutritional_info.sugars_g ? Number(form.nutritional_info.sugars_g) : undefined,
          proteins_g: form.nutritional_info.proteins_g ? Number(form.nutritional_info.proteins_g) : undefined,
          fiber_g: form.nutritional_info.fiber_g ? Number(form.nutritional_info.fiber_g) : undefined,
          salt_g: form.nutritional_info.salt_g ? Number(form.nutritional_info.salt_g) : undefined,
        },
      });
      onClose();
    } catch (err) {
      console.error('Error saving recipe:', err);
      toast.error('Error al desar la fitxa tècnica');
    } finally {
      setSaving(false);
    }
  };

  // AI generation handler
  const generateDetails = async () => {
    if (!form.description && !form.name) {
      toast.warning('Afegiu una descripció o nom del producte per generar detalls');
      return;
    }
    setGeneratingDetails(true);
    try {
      const { data } = await hosys.functions.invoke('generateRecipeDetails', {
        description: form.description,
        productName: form.name,
        category: form.category,
        ingredients: form.ingredients.filter(i => i.name),
      });
      if (data.error) { toast.error(data.error); return; }
      const updates = { ...form };
      if (data.ingredients?.length > 0) {
        updates.ingredients = data.ingredients.map(i => ({
          name: i.name || "", quantity: String(i.quantity || ""),
          unit: i.unit || "g", unit_cost: String(i.unit_cost || ""),
        }));
      }
      if (data.allergens?.length > 0) updates.allergens = data.allergens;
      if (data.preparation_steps) updates.preparation_steps = data.preparation_steps;
      if (data.preparation_time) updates.preparation_time = String(data.preparation_time);
      if (data.estimated_cost) updates.estimated_cost = String(data.estimated_cost);
      if (data.shelf_life_months) updates.shelf_life_months = String(data.shelf_life_months);
      if (data.storage_temperature) updates.storage_temperature = data.storage_temperature;
      if (data.storage_notes) updates.storage_notes = data.storage_notes;
      if (data.nutritional_info) {
        updates.nutritional_info = {
          ...form.nutritional_info,
          ...Object.fromEntries(
            Object.entries(data.nutritional_info).map(([k, v]) => [k, String(v || "0")])
          ),
        };
      }
      if (data.organoleptic_characteristics) {
        updates.organoleptic_characteristics = {
          ...form.organoleptic_characteristics,
          ...data.organoleptic_characteristics,
        };
      }
      setForm(updates);
      toast.success('Fitxa tècnica generada amb IA correctament!');
    } catch (err) {
      console.error('Error generating details:', err);
      toast.error('Error generant la fitxa tècnica');
    } finally {
      setGeneratingDetails(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="recipe-form-title">
            {editData ? "Editar Fitxa Tècnica" : "Nova Fitxa Tècnica"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <RecipeBasicInfo
            form={form}
            setForm={setForm}
            photoInputRef={photoInputRef}
            uploadingPhoto={uploadingPhoto}
            handlePhotoUpload={handlePhotoUpload}
            generatingDetails={generatingDetails}
            generateDetails={generateDetails}
            totalIngGrams={totalIngGrams}
          />
          <RecipeIngredients
            form={form}
            setForm={setForm}
            ingredientCatalog={ingredientCatalog}
            ingSearch={ingSearch}
            setIngSearch={setIngSearch}
            addIngredient={addIngredient}
            removeIngredient={removeIngredient}
            updateIngredient={updateIngredient}
            handleIngCostBlur={handleIngCostBlur}
            selectFromCatalog={selectFromCatalog}
            totalIngGrams={totalIngGrams}
            autoIngCost={autoIngCost}
            hasIngCosts={hasIngCosts}
          />
          <RecipePricing
            form={form}
            setForm={setForm}
            effectiveCost={effectiveCost}
            autoIngCost={autoIngCost}
            hasIngCosts={hasIngCosts}
          />
          <RecipeNutrition
            form={form}
            setForm={setForm}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="recipe-form-cancel-btn">Cancel·lar</Button>
          <Button
            onClick={handleSave}
            disabled={!form.name || saving}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
            data-testid="recipe-form-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editData ? "Actualitzar" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
